import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Pagination, Row, Col, Alert, Space } from 'antd';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import './ProductsPage.css';

const { TextArea } = Input;

function ProductsPage() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [addModalError, setAddModalError] = useState('');
  const [addModalSuccess, setAddModalSuccess] = useState('');
  const [editModalError, setEditModalError] = useState('');
  const [editModalSuccess, setEditModalSuccess] = useState('');
  const [addedQuantity, setAddedQuantity] = useState(0);

  const [newProduct, setNewProduct] = useState({
    type: '',
   
    size: '',
    price: '',
    quantity: '',
  
    category_id: ''
  });

  const [updatedProduct, setUpdatedProduct] = useState({
    type: '',
 
    size: '',
    price: '',
    quantity: '',
  
    category_id: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get('https://agpipefitting.org.et/api/products'),
          axios.get('https://agpipefitting.org.et/api/categories')
        ]);
        setProductsData(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || 'Failed to load data.');
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    console.log('Products data updated:', productsData);
  }, [productsData]);  // This will log and re-render whenever productsData changes.
   
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => record.price ? `ETB ${record.price}` : '-',
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
    },
   
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            shape="circle"
            icon={<FaEdit className="action-icon" />}
            onClick={() => handleShowEditModal(record.id)}
            className="edit-btn"
          />
          <Button 
            shape="circle"
            icon={<FaTrashAlt className="action-icon" />}
            onClick={() => handleDeleteProduct(record.id)}
            danger
            className="delete-btn"
          />
        </Space>
      ),
    },
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const filteredProducts = productsData.filter(product => {
    const searchLower = searchQuery.toLowerCase();
    return (
      product.type?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower)
    );
  });
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handleShowEditModal = (productId) => {
    const product = productsData.find(item => item.id === productId);
    setEditingProduct(product);
    setUpdatedProduct({ 
      ...product,
      image: null,
      category_id: product.category_id || ''
    });
    setAddedQuantity(0);
    setShowEditModal(true);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setAddModalError('');
    
    try {
      const formData = new FormData();
      Object.entries(newProduct).forEach(([key, value]) => {
        if (value !== null && value !== '') formData.append(key, value);
      });
  
      const response = await axios.post('https://agpipefitting.org.et/api/products', formData);
  
      // Using a callback function to update state, ensuring state is properly updated
      setProductsData(prevProductsData => [...prevProductsData, response.data]);
  
      setAddModalSuccess('Product added successfully!');
      
      setTimeout(() => {
        setShowAddModal(false);
        setNewProduct({
          type: '', size: '', 
          price: '', quantity: '', category_id: ''
        });
        setSuccessMessage('Product added successfully!');
      }, 1500);
    } catch (error) {
      setAddModalError(error.response?.data?.message || 'Error adding product');
    }
  };
  

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setEditModalError('');
    try {
      const formData = new FormData();
      const newTotalQuantity = Number(editingProduct.quantity) + Number(addedQuantity);

      Object.entries(updatedProduct).forEach(([key, value]) => {
        if (key === 'image' && value instanceof File) {
          formData.append(key, value);
        } else if (value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      formData.append('quantity', newTotalQuantity);
      const response = await axios.post(
        `https://agpipefitting.org.et/api/products/${editingProduct.id}`,
        formData,
        { headers: { 'X-HTTP-Method-Override': 'PUT' } }
      );

      setProductsData(productsData.map(p => 
        p.id === editingProduct.id ? response.data : p
      ));
      setEditModalSuccess(`Added ${addedQuantity} units. New total: ${response.data.quantity}`);
      setTimeout(() => setShowEditModal(false), 1500);
    } catch (error) {
      setEditModalError(error.response?.data?.message || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`https://agpipefitting.org.et/api/products/${productId}`);
        setProductsData(productsData.filter(item => item.id !== productId));
        setSuccessMessage('Product deleted successfully!');
      } catch (error) {
        setErrorMessage('Error deleting product. Please try again.');
      }
    }
  };

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  return (
    <div className="modern-container">
      <div className="page-header">
        <h1 className="page-title">Product Management</h1>
        <Button 
          type="primary" 
          onClick={() => setShowAddModal(true)}
          icon={<FaPlus className="btn-icon" />}
          className="add-product-btn"
        >
          Add Product
        </Button>
      </div>

      <div className="search-bar-container">
        <Input.Search
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="modern-search"
          enterButton
        />
      </div>

      <div className="alerts-container">
        {successMessage && <Alert message={successMessage} type="success" showIcon closable />}
        {errorMessage && <Alert message={errorMessage} type="error" showIcon closable />}
      </div>

      <Table
        columns={columns}
        dataSource={currentProducts}
        pagination={false}
        rowKey="id"
        className="modern-table"
        bordered
      />

      <Pagination
        current={currentPage}
        total={filteredProducts.length}
        pageSize={itemsPerPage}
        onChange={(page) => setCurrentPage(page)}
        className="modern-pagination"
        showSizeChanger={false}
      />

      {/* Add Product Modal */}
      <Modal
        title={<span className="modal-title">Add New Product</span>}
        visible={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        className="modern-modal"
        width={800}
      >
        <Form {...formItemLayout} onSubmitCapture={handleAddProduct}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Product Name" required>
                <Input
                  value={newProduct.type}
                  onChange={(e) => setNewProduct({...newProduct, type: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Category" required>
                <Select
                  value={newProduct.category_id}
                  onChange={(value) => setNewProduct({...newProduct, category_id: value})}
                  className="modern-input"
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Price" required>
                <Input
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Size" required>
                <Input
                  value={newProduct.size}
                  onChange={(e) => setNewProduct({...newProduct, size: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Initial Quantity" required>
                <Input
                  type="number"
                  value={newProduct.quantity}
                  onChange={(e) => setNewProduct({...newProduct, quantity: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
           
          </Row>

         

          <div className="form-alerts">
            {addModalError && <Alert message={addModalError} type="error" showIcon />}
            {addModalSuccess && <Alert message={addModalSuccess} type="success" showIcon />}
          </div>

          <Button 
            type="primary" 
            htmlType="submit" 
            className="submit-btn"
            block
          >
            Add Product
          </Button>
        </Form>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        title={<span className="modal-title">Edit Product</span>}
        visible={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        className="modern-modal"
        width={800}
      >
        <Form {...formItemLayout} onSubmitCapture={handleUpdateProduct}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Product Name" required>
                <Input
                  value={updatedProduct.type}
                  onChange={(e) => setUpdatedProduct({...updatedProduct, type: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Category" required>
                <Select
                  value={updatedProduct.category_id}
                  onChange={(value) => setUpdatedProduct({...updatedProduct, category_id: value})}
                  className="modern-input"
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Price" required>
                <Input
                  type="number"
                  value={updatedProduct.price}
                  onChange={(e) => setUpdatedProduct({...updatedProduct, price: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Size" required>
                <Input
                  value={updatedProduct.size}
                  onChange={(e) => setUpdatedProduct({...updatedProduct, size: e.target.value})}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Current Quantity">
                <Input
                  value={editingProduct?.quantity}
                  disabled
                  className="modern-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Add Quantity">
                <Input
                  type="number"
                  value={addedQuantity}
                  onChange={(e) => setAddedQuantity(e.target.value)}
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

         

        

          <div className="form-alerts">
            {editModalError && <Alert message={editModalError} type="error" showIcon />}
            {editModalSuccess && <Alert message={editModalSuccess} type="success" showIcon />}
          </div>

          <Button 
            type="primary" 
            htmlType="submit" 
            className="submit-btn"
            block
          >
            Update Product
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

export default ProductsPage;